import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Briefcase, Plus, Trash2, TrendingUp, TrendingDown, X, BarChart3 } from 'lucide-react';
import Card from '../components/Card';
import PriceChange from '../components/PriceChange';
import LoadingSpinner from '../components/LoadingSpinner';
import PortfolioPerformanceChart from '../components/PortfolioPerformanceChart';
import SignalBadge from '../components/SignalBadge';
import AIRecommendationBadge from '../components/AIRecommendationBadge';
import eodhd from '../services/eodhd';
import api from '../services/api';
import { formatCurrency } from '../utils/helpers';
import type { SignalType, SignalStrength, Portfolio as APIPortfolio, Transaction, TransactionType } from '../services/api';
import { usePermission } from '../hooks/usePermission';

interface Holding {
  id?: number; // Backend holding ID
  symbol: string;
  name: string;
  quantity: number;
  avgCost: number;
  currentPrice: number;
  change: number;
  value: number;
  pnl: number;
  pnlPercent: number;
  currency: string;
  signal?: SignalType;
  signalStrength?: string;
  signalConfidence?: number;
  strategyMatch?: boolean;
  matchedStrategy?: string;
  aiRecommendation?: 'strong_buy' | 'buy' | 'hold' | 'sell' | 'strong_sell';
  aiConfidence?: number;
}

// Transaction interface for future use
// interface Transaction {
//   id: string;
//   symbol: string;
//   type: 'buy' | 'sell';
//   quantity: number;
//   price: number;
//   date: string;
// }

type ChartPeriod = '1W' | '1M' | '3M' | '6M' | '1Y' | 'ALL';

const Portfolio: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [portfolioId, setPortfolioId] = useState<number | null>(null);
  const [holdings, setHoldings] = useState<Holding[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [newHolding, setNewHolding] = useState({ symbol: '', quantity: '', avgCost: '' });
  const [newTransaction, setNewTransaction] = useState({
    symbol: '',
    type: 'buy' as TransactionType,
    quantity: '',
    price: '',
    fees: '',
    notes: '',
    date: new Date().toISOString().split('T')[0],
  });
  const [chartPeriod, setChartPeriod] = useState<ChartPeriod>('1M');
  const [analyzingSymbol, setAnalyzingSymbol] = useState<string | null>(null);
  const { allowed: hasStrategyPermission } = usePermission('feature:strategy-driven-signals');

  const [portfolioStats, setPortfolioStats] = useState({
    totalValue: 0,
    totalCost: 0,
    totalPnL: 0,
    totalPnLPercent: 0,
    dayChange: 0,
    dayChangePercent: 0,
  });

  useEffect(() => {
    loadPortfolioData();
  }, []);

  const loadPortfolioData = async () => {
    try {
      setLoading(true);

      // Fetch portfolios from backend
      let portfolios: APIPortfolio[];
      try {
        portfolios = await api.getPortfolios();
      } catch (error) {
        console.error('Error fetching portfolios:', error);
        setLoading(false);
        return;
      }

      // Create default portfolio if none exists
      if (!portfolios || portfolios.length === 0) {
        try {
          const newPortfolio = await api.createPortfolio('My Portfolio', 'Primary investment portfolio');
          portfolios = [newPortfolio];
        } catch (error) {
          console.error('Error creating portfolio:', error);
          setLoading(false);
          return;
        }
      }

      // Use first portfolio
      const portfolio = portfolios[0];
      setPortfolioId(portfolio.id);

      // Load holdings with current prices and signals
      const holdingPromises = portfolio.holdings.map(async (h) => {
        try {
          const data = await eodhd.getRealTimePrice(h.symbol);
          const currentPrice = data.close || 0;
          const value = currentPrice * Number(h.quantity);
          const cost = Number(h.avg_cost) * Number(h.quantity);
          const pnl = value - cost;
          const pnlPercent = cost > 0 ? (pnl / cost) * 100 : 0;

          // Fetch signal recommendation
          let signal: SignalType | undefined;
          let signalStrength: SignalStrength | undefined;
          let signalConfidence: number | undefined;
          let strategyMatch: boolean | undefined;
          let matchedStrategy: string | undefined;
          try {
            const signalData = await api.getSignal(h.symbol);
            signal = signalData.signal;
            signalStrength = signalData.strength;
            signalConfidence = signalData.confidence;
            strategyMatch = signalData.strategy_match;
            matchedStrategy = signalData.matched_strategy;
          } catch (err) {
            console.error(`Failed to fetch signal for ${h.symbol}:`, err);
          }

          // Fetch AI recommendation
          let aiRecommendation: 'strong_buy' | 'buy' | 'hold' | 'sell' | 'strong_sell' | undefined;
          let aiConfidence: number | undefined;
          try {
            const aiData = await api.getLatestAIAnalysis(h.symbol);
            aiRecommendation = aiData.recommendation;
            aiConfidence = aiData.confidence;
          } catch (err) {
            // Silently fail - AI recommendation is optional
            console.debug(`No AI recommendation for ${h.symbol}`);
          }

          return {
            id: h.id,
            symbol: h.symbol,
            name: h.symbol.split('.')[0],
            quantity: Number(h.quantity),
            avgCost: Number(h.avg_cost),
            currentPrice,
            change: data.change_percent ?? data.change_p ?? 0,
            value,
            pnl,
            pnlPercent,
            currency: h.symbol.includes('JSE') ? 'R' : '$',
            signal,
            signalStrength,
            signalConfidence,
            strategyMatch,
            matchedStrategy,
            aiRecommendation,
            aiConfidence,
          };
        } catch (error) {
          console.error(`Error loading holding ${h.symbol}:`, error);
          // Return basic holding data without live prices
          const value = Number(h.avg_cost) * Number(h.quantity);
          return {
            id: h.id,
            symbol: h.symbol,
            name: h.symbol.split('.')[0],
            quantity: Number(h.quantity),
            avgCost: Number(h.avg_cost),
            currentPrice: Number(h.avg_cost),
            change: 0,
            value,
            pnl: 0,
            pnlPercent: 0,
            currency: h.symbol.includes('JSE') ? 'R' : '$',
          };
        }
      });

      const results = await Promise.all(holdingPromises);
      setHoldings(results);

      // Fetch transactions
      try {
        const portfolioTransactions = await api.getTransactions(portfolio.id);
        setTransactions(portfolioTransactions);
      } catch (error) {
        console.error('Error fetching transactions:', error);
        setTransactions([]);
      }

      // Calculate portfolio stats
      const totalValue = results.reduce((sum, h) => sum + h.value, 0);
      const totalCost = results.reduce((sum, h) => sum + h.avgCost * h.quantity, 0);
      const totalPnL = totalValue - totalCost;
      const dayChange = results.reduce((sum, h) => sum + (h.value * h.change) / 100, 0);

      setPortfolioStats({
        totalValue,
        totalCost,
        totalPnL,
        totalPnLPercent: totalCost > 0 ? (totalPnL / totalCost) * 100 : 0,
        dayChange,
        dayChangePercent: totalValue > 0 ? (dayChange / totalValue) * 100 : 0,
      });
    } catch (error) {
      console.error('Error loading portfolio:', error);
    } finally {
      setLoading(false);
    }
  };

  const triggerAIAnalysis = async (e: React.MouseEvent, symbol: string) => {
    e.stopPropagation();
    if (analyzingSymbol === symbol) return;
    setAnalyzingSymbol(symbol);
    try {
      const result = await api.getAIAnalysis(symbol, 'comprehensive');
      setHoldings(prev => prev.map(h =>
        h.symbol === symbol
          ? { ...h, aiRecommendation: result.recommendation, aiConfidence: result.confidence }
          : h
      ));
    } catch (error) {
      console.error(`AI analysis failed for ${symbol}:`, error);
    } finally {
      setAnalyzingSymbol(null);
    }
  };

  const addHolding = async () => {
    if (!newHolding.symbol || !newHolding.quantity || !newHolding.avgCost || !portfolioId) return;

    try {
      await api.addHolding(portfolioId, {
        symbol: newHolding.symbol.toUpperCase(),
        quantity: parseFloat(newHolding.quantity),
        average_cost: parseFloat(newHolding.avgCost),
      });

      setNewHolding({ symbol: '', quantity: '', avgCost: '' });
      setShowAddModal(false);
      await loadPortfolioData();
    } catch (error) {
      console.error('Error adding holding:', error);
      alert('Failed to add holding. Please try again.');
    }
  };

  const removeHolding = async (holdingId: number) => {
    if (!portfolioId) return;

    try {
      await api.deleteHolding(portfolioId, holdingId);
      await loadPortfolioData();
    } catch (error) {
      console.error('Error removing holding:', error);
      alert('Failed to remove holding. Please try again.');
    }
  };

  const addTransaction = async () => {
    if (!newTransaction.symbol || !newTransaction.quantity || !newTransaction.price || !portfolioId) return;

    try {
      await api.addTransaction(portfolioId, {
        symbol: newTransaction.symbol.toUpperCase(),
        transaction_type: newTransaction.type,
        quantity: parseFloat(newTransaction.quantity),
        price: parseFloat(newTransaction.price),
        fees: newTransaction.fees ? parseFloat(newTransaction.fees) : 0,
        notes: newTransaction.notes || undefined,
        transaction_date: newTransaction.date ? new Date(newTransaction.date).toISOString() : undefined,
      });

      setNewTransaction({
        symbol: '',
        type: 'buy',
        quantity: '',
        price: '',
        fees: '',
        notes: '',
        date: new Date().toISOString().split('T')[0],
      });
      setShowTransactionModal(false);
      await loadPortfolioData();
    } catch (error) {
      console.error('Error adding transaction:', error);
      alert('Failed to add transaction. Please try again.');
    }
  };

  // Calculate allocation percentages
  const allocation = holdings.map((h) => ({
    symbol: h.symbol.split('.')[0],
    value: h.value,
    percent: portfolioStats.totalValue > 0 ? (h.value / portfolioStats.totalValue) * 100 : 0,
  }));

  const allocationColors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

  return (
    <div className="p-3 sm:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Briefcase className="text-teal-400" />
            Portfolio
          </h1>
          <p className="text-sm text-slate-400 mt-1">Track your investments and performance</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setShowTransactionModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white font-semibold rounded-lg transition-colors"
          >
            <Plus size={16} />
            Record Transaction
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-teal-500 hover:bg-teal-400 text-slate-900 font-semibold rounded-lg transition-colors"
          >
            <Plus size={16} />
            Add Holding
          </button>
        </div>
      </div>

      {loading ? (
        <LoadingSpinner size="lg" />
      ) : (
        <>
          {/* Portfolio Summary */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-gradient-to-br from-slate-800 to-slate-800/50 rounded-xl border border-slate-700/50 p-6">
              <p className="text-sm text-slate-400 mb-1">Total Value</p>
              <p className="text-3xl font-bold font-mono">{formatCurrency(portfolioStats.totalValue)}</p>
            </div>
            <div className="bg-slate-800 rounded-xl border border-slate-700/50 p-6">
              <p className="text-sm text-slate-400 mb-1">Total P&L</p>
              <p
                className={`text-2xl font-bold font-mono ${
                  portfolioStats.totalPnL >= 0 ? 'text-emerald-400' : 'text-red-400'
                }`}
              >
                {portfolioStats.totalPnL >= 0 ? '+' : ''}
                {formatCurrency(portfolioStats.totalPnL)}
              </p>
              <PriceChange value={portfolioStats.totalPnLPercent} size="sm" />
            </div>
            <div className="bg-slate-800 rounded-xl border border-slate-700/50 p-6">
              <p className="text-sm text-slate-400 mb-1">Today's Change</p>
              <p
                className={`text-2xl font-bold font-mono ${
                  portfolioStats.dayChange >= 0 ? 'text-emerald-400' : 'text-red-400'
                }`}
              >
                {portfolioStats.dayChange >= 0 ? '+' : ''}
                {formatCurrency(portfolioStats.dayChange)}
              </p>
              <PriceChange value={portfolioStats.dayChangePercent} size="sm" />
            </div>
            <div className="bg-slate-800 rounded-xl border border-slate-700/50 p-6">
              <p className="text-sm text-slate-400 mb-1">Cost Basis</p>
              <p className="text-2xl font-bold font-mono">{formatCurrency(portfolioStats.totalCost)}</p>
            </div>
          </div>

          {/* Performance Chart */}
          <Card
            title={
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center gap-2">
                  <BarChart3 size={18} className="text-teal-400" />
                  <span>Portfolio Performance</span>
                </div>
                <div className="flex gap-1">
                  {(['1W', '1M', '3M', '6M', '1Y', 'ALL'] as ChartPeriod[]).map((period) => (
                    <button
                      key={period}
                      onClick={() => setChartPeriod(period)}
                      className={`px-2 py-1 text-xs rounded transition-colors ${
                        chartPeriod === period
                          ? 'bg-teal-500 text-slate-900 font-semibold'
                          : 'bg-slate-700/50 text-slate-400 hover:bg-slate-700'
                      }`}
                    >
                      {period}
                    </button>
                  ))}
                </div>
              </div>
            }
          >
            {portfolioStats.totalValue > 0 ? (
              <PortfolioPerformanceChart
                currentValue={portfolioStats.totalValue}
                costBasis={portfolioStats.totalCost}
                period={chartPeriod}
              />
            ) : (
              <div className="h-64 flex items-center justify-center text-slate-500">
                Add holdings to see performance chart
              </div>
            )}
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Holdings Table */}
            <div className="lg:col-span-2">
              <Card title="Holdings">
                {holdings.length === 0 ? (
                  <div className="text-center py-12">
                    <Briefcase size={48} className="mx-auto text-slate-600 mb-4" />
                    <p className="text-slate-400">No holdings yet</p>
                    <button
                      onClick={() => setShowAddModal(true)}
                      className="mt-4 text-teal-400 hover:text-teal-300 text-sm"
                    >
                      + Add your first holding
                    </button>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="text-left text-xs text-slate-500 uppercase border-b border-slate-700/50">
                          <th className="pb-3 font-medium">Symbol</th>
                          <th className="pb-3 font-medium text-right">Quantity</th>
                          <th className="pb-3 font-medium text-right">Avg Cost</th>
                          <th className="pb-3 font-medium text-right">Price</th>
                          <th className="pb-3 font-medium text-right">Value</th>
                          <th className="pb-3 font-medium text-right">P&L</th>
                          <th className="pb-3 font-medium text-right">Today</th>
                          <th className="pb-3 font-medium text-center">Action</th>
                          <th className="pb-3 font-medium text-center">AI</th>
                          <th className="pb-3 font-medium"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {holdings.map((holding, i) => (
                          <tr
                            key={i}
                            className="border-t border-slate-700/50 hover:bg-slate-700/30 cursor-pointer transition-colors"
                            onClick={() => navigate(`/asset/${holding.symbol}`)}
                          >
                            <td className="py-3">
                              <span className="font-mono font-medium text-teal-400">{holding.name}</span>
                            </td>
                            <td className="py-3 text-right font-mono text-sm">{holding.quantity}</td>
                            <td className="py-3 text-right font-mono text-sm text-slate-400">
                              {holding.currency}
                              {holding.avgCost.toFixed(2)}
                            </td>
                            <td className="py-3 text-right font-mono text-sm">
                              {holding.currency}
                              {holding.currentPrice.toFixed(2)}
                            </td>
                            <td className="py-3 text-right font-mono text-sm">
                              {holding.currency}
                              {holding.value.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </td>
                            <td className="py-3 text-right">
                              <div
                                className={`font-mono text-sm ${
                                  holding.pnl >= 0 ? 'text-emerald-400' : 'text-red-400'
                                }`}
                              >
                                {holding.pnl >= 0 ? '+' : ''}
                                {holding.currency}
                                {holding.pnl.toFixed(2)}
                              </div>
                              <div className="text-xs text-slate-500">
                                {holding.pnlPercent >= 0 ? '+' : ''}
                                {holding.pnlPercent.toFixed(1)}%
                              </div>
                            </td>
                            <td className="py-3">
                              <div className="flex justify-end">
                                <PriceChange value={holding.change} size="sm" />
                              </div>
                            </td>
                            <td className="py-3 text-center">
                              {holding.signal ? (
                                <div onClick={(e) => e.stopPropagation()}>
                                  <SignalBadge
                                    signal={holding.signal}
                                    strength={holding.signalStrength as SignalStrength ?? 'moderate'}
                                    confidence={holding.signalConfidence ?? 0}
                                    size="sm"
                                    strategyMatch={hasStrategyPermission ? holding.strategyMatch : undefined}
                                    strategyName={hasStrategyPermission ? holding.matchedStrategy : undefined}
                                  />
                                </div>
                              ) : (
                                <span className="text-xs text-slate-500">Loading...</span>
                              )}
                            </td>
                            <td className="py-3 text-center">
                              {analyzingSymbol === holding.symbol ? (
                                <span className="text-xs text-teal-400 animate-pulse" title="Running AI analysis...">Analyzing...</span>
                              ) : holding.aiRecommendation ? (
                                <button
                                  type="button"
                                  onClick={(e) => triggerAIAnalysis(e, holding.symbol)}
                                  className="bg-transparent border-0 p-0 cursor-pointer opacity-90 hover:opacity-100"
                                  title="Click to refresh AI analysis"
                                >
                                  <AIRecommendationBadge
                                    recommendation={holding.aiRecommendation}
                                    confidence={holding.aiConfidence}
                                    size="sm"
                                    showIcon={false}
                                  />
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  onClick={(e) => triggerAIAnalysis(e, holding.symbol)}
                                  className="text-xs text-teal-400 hover:text-teal-300 transition-colors"
                                  title="Click to run AI analysis"
                                >
                                  Analyze
                                </button>
                              )}
                            </td>
                            <td className="py-3">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (holding.id) {
                                    removeHolding(holding.id);
                                  }
                                }}
                                className="p-1 hover:bg-red-500/20 hover:text-red-400 rounded transition-colors"
                              >
                                <Trash2 size={14} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>
            </div>

            {/* Allocation Chart */}
            <div className="space-y-6">
              <Card title="Allocation">
                <div className="space-y-4">
                  {/* Simple bar chart */}
                  <div className="flex h-4 rounded-full overflow-hidden">
                    {allocation.map((item, i) => (
                      <div
                        key={i}
                        style={{
                          width: `${item.percent}%`,
                          backgroundColor: allocationColors[i % allocationColors.length],
                        }}
                        className="first:rounded-l-full last:rounded-r-full"
                        title={`${item.symbol}: ${item.percent.toFixed(1)}%`}
                      />
                    ))}
                  </div>

                  {/* Legend */}
                  <div className="space-y-2">
                    {allocation.map((item, i) => (
                      <div key={i} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: allocationColors[i % allocationColors.length] }}
                          />
                          <span className="text-sm">{item.symbol}</span>
                        </div>
                        <span className="text-sm font-mono text-slate-400">{item.percent.toFixed(1)}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              </Card>

              {/* Performance Summary */}
              <Card title="Performance">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-400">Winners</span>
                    <div className="flex items-center gap-2">
                      <TrendingUp size={14} className="text-emerald-400" />
                      <span className="text-emerald-400 font-mono">
                        {holdings.filter((h) => h.pnl > 0).length}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-400">Losers</span>
                    <div className="flex items-center gap-2">
                      <TrendingDown size={14} className="text-red-400" />
                      <span className="text-red-400 font-mono">
                        {holdings.filter((h) => h.pnl < 0).length}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-400">Best Performer</span>
                    <span className="text-sm font-mono text-emerald-400">
                      {holdings.length > 0
                        ? holdings.reduce((best, h) => (h.pnlPercent > best.pnlPercent ? h : best)).name
                        : '—'}
                    </span>
                  </div>
                </div>
              </Card>
            </div>
          </div>

          {/* Transaction History */}
          <Card title="Transaction History">
            {transactions.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-slate-400 text-sm">No transactions recorded yet</p>
                <button
                  onClick={() => setShowTransactionModal(true)}
                  className="mt-3 text-teal-400 hover:text-teal-300 text-sm"
                >
                  + Record your first transaction
                </button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-xs text-slate-500 uppercase border-b border-slate-700/50">
                      <th className="pb-3 font-medium">Date</th>
                      <th className="pb-3 font-medium">Symbol</th>
                      <th className="pb-3 font-medium">Type</th>
                      <th className="pb-3 font-medium text-right">Quantity</th>
                      <th className="pb-3 font-medium text-right">Price</th>
                      <th className="pb-3 font-medium text-right">Fees</th>
                      <th className="pb-3 font-medium text-right">Total</th>
                      <th className="pb-3 font-medium">Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map((tx) => {
                      const total = tx.quantity * tx.price + (tx.fees || 0);
                      const isBuy = tx.transaction_type === 'buy';
                      return (
                        <tr
                          key={tx.id}
                          className="border-t border-slate-700/50 hover:bg-slate-700/30 transition-colors"
                        >
                          <td className="py-3 text-sm">
                            {new Date(tx.transaction_date).toLocaleDateString()}
                          </td>
                          <td className="py-3">
                            <span className="font-mono font-medium text-teal-400">
                              {tx.symbol}
                            </span>
                          </td>
                          <td className="py-3">
                            <span
                              className={`px-2 py-1 text-xs font-semibold rounded ${
                                isBuy ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'
                              }`}
                            >
                              {tx.transaction_type.toUpperCase()}
                            </span>
                          </td>
                          <td className="py-3 text-right font-mono text-sm">{tx.quantity}</td>
                          <td className="py-3 text-right font-mono text-sm">
                            {formatCurrency(tx.price)}
                          </td>
                          <td className="py-3 text-right font-mono text-sm text-slate-400">
                            {formatCurrency(tx.fees || 0)}
                          </td>
                          <td className="py-3 text-right font-mono text-sm font-semibold">
                            {formatCurrency(total)}
                          </td>
                          <td className="py-3 text-sm text-slate-400 max-w-xs truncate">
                            {tx.notes || '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}

      {/* Add Holding Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-xl p-6 w-full max-w-sm mx-4 sm:mx-0 border border-slate-700">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Add Holding</h3>
              <button onClick={() => setShowAddModal(false)} className="text-slate-400 hover:text-white">
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-sm text-slate-400 block mb-1">Symbol</label>
                <input
                  type="text"
                  placeholder="e.g., AAPL.US, NPN.JSE"
                  value={newHolding.symbol}
                  onChange={(e) => setNewHolding({ ...newHolding, symbol: e.target.value })}
                  className="w-full bg-slate-700 px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <div>
                <label className="text-sm text-slate-400 block mb-1">Quantity</label>
                <input
                  type="number"
                  placeholder="10"
                  value={newHolding.quantity}
                  onChange={(e) => setNewHolding({ ...newHolding, quantity: e.target.value })}
                  className="w-full bg-slate-700 px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <div>
                <label className="text-sm text-slate-400 block mb-1">Average Cost</label>
                <input
                  type="number"
                  placeholder="150.00"
                  value={newHolding.avgCost}
                  onChange={(e) => setNewHolding({ ...newHolding, avgCost: e.target.value })}
                  className="w-full bg-slate-700 px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowAddModal(false)}
                className="flex-1 px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={addHolding}
                className="flex-1 px-4 py-2 bg-teal-500 hover:bg-teal-400 text-slate-900 font-semibold rounded-lg transition-colors"
              >
                Add Holding
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Record Transaction Modal */}
      {showTransactionModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-xl p-6 w-full max-w-[500px] mx-4 sm:mx-0 border border-slate-700">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Record Transaction</h3>
              <button onClick={() => setShowTransactionModal(false)} className="text-slate-400 hover:text-white">
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-slate-400 block mb-1">Symbol</label>
                  <input
                    type="text"
                    placeholder="e.g., AAPL.US, NPN.JSE"
                    value={newTransaction.symbol}
                    onChange={(e) => setNewTransaction({ ...newTransaction, symbol: e.target.value })}
                    className="w-full bg-slate-700 px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div>
                  <label className="text-sm text-slate-400 block mb-1">Type</label>
                  <select
                    value={newTransaction.type}
                    onChange={(e) => setNewTransaction({ ...newTransaction, type: e.target.value as TransactionType })}
                    className="w-full bg-slate-700 px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                  >
                    <option value="buy">Buy</option>
                    <option value="sell">Sell</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-slate-400 block mb-1">Quantity</label>
                  <input
                    type="number"
                    placeholder="10"
                    value={newTransaction.quantity}
                    onChange={(e) => setNewTransaction({ ...newTransaction, quantity: e.target.value })}
                    className="w-full bg-slate-700 px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div>
                  <label className="text-sm text-slate-400 block mb-1">Price per Share</label>
                  <input
                    type="number"
                    step="0.01"
                    placeholder="150.00"
                    value={newTransaction.price}
                    onChange={(e) => setNewTransaction({ ...newTransaction, price: e.target.value })}
                    className="w-full bg-slate-700 px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-slate-400 block mb-1">Fees (optional)</label>
                  <input
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={newTransaction.fees}
                    onChange={(e) => setNewTransaction({ ...newTransaction, fees: e.target.value })}
                    className="w-full bg-slate-700 px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div>
                  <label className="text-sm text-slate-400 block mb-1">Transaction Date</label>
                  <input
                    type="date"
                    value={newTransaction.date}
                    onChange={(e) => setNewTransaction({ ...newTransaction, date: e.target.value })}
                    className="w-full bg-slate-700 px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm text-slate-400 block mb-1">Notes (optional)</label>
                <textarea
                  placeholder="Add notes about this transaction..."
                  value={newTransaction.notes}
                  onChange={(e) => setNewTransaction({ ...newTransaction, notes: e.target.value })}
                  rows={3}
                  className="w-full bg-slate-700 px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none"
                />
              </div>

              {newTransaction.quantity && newTransaction.price && (
                <div className="bg-slate-700/50 p-4 rounded-lg">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Total</span>
                    <span className="font-mono font-semibold">
                      {formatCurrency(
                        parseFloat(newTransaction.quantity || '0') * parseFloat(newTransaction.price || '0') +
                          parseFloat(newTransaction.fees || '0')
                      )}
                    </span>
                  </div>
                </div>
              )}
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowTransactionModal(false)}
                className="flex-1 px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={addTransaction}
                className="flex-1 px-4 py-2 bg-teal-500 hover:bg-teal-400 text-slate-900 font-semibold rounded-lg transition-colors"
              >
                Record Transaction
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Portfolio;
